<template>
  <div
    :style="{
      background: `linear-gradient(90deg, ${brandColour} 0%, ${getDarkPrimary()} 100%)`,
      color: textColour,
    }"
    class="home-container"
  >
    <div class="home-content">
      <HomeScreenHeader
        :brandColour="brandColour"
        :homescreenImageUrl="homescreenImageUrl"
        :greetingText="greetingText"
        :introductionText="introductionText"
      />
      <div class="body">
        <HomeScreenPanelStartChat
          :brandColour="brandColour"
          :actionColour="actionColour"
          :subtitle="typicalResponseTime"
        />
        <CardRenderer :colour="actionColour" :cards="cards" :location="'homescreen'" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, ref, watch } from 'vue';
import { default as ColourPicker } from '@/shared/services/color';
import { HomescreenAppInitViewModel } from '@/features/webassistant/models';
import HomeScreenHeader from './HomeScreenHeader.vue';
import HomeScreenPanelStartChat from './HomeScreenPanelStartChat.vue';
import CardRenderer from '@/shared/components/cardRenderer/CardRenderer.vue';
import { Card } from '@/shared/components/cardRenderer';

const props = defineProps({
  brandColour: {
    type: String,
    required: true,
  },
  actionColour: {
    type: String,
    required: true,
  },
  homescreenImageUrl: {
    type: String,
    default: '',
  },
  greetingText: {
    type: String,
  },
  introductionText: {
    type: String,
  },
  typicalResponseTime: {
    type: String,
  },
  homescreenApps: {
    type: Array as PropType<HomescreenAppInitViewModel[]>,
    default: [],
  },
});

const cards = ref<Card[]>([]);
const textColour = computed(() => ColourPicker.getContrastingColour(props.brandColour));

watch(
  () => props.homescreenApps,
  (value: HomescreenAppInitViewModel[]) => {
    cards.value = value.map((app) => ({ id: app.id, content: app.card }));
  },
);

function getDarkPrimary() {
  return ColourPicker.darken(props.brandColour, 12);
}
</script>

<style lang="scss" scoped>
.home-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  height: 100%;

  .home-content {
    overflow-y: auto;
    overflow-x: hidden;
    flex: 1;
    scrollbar-color: #eee transparent;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;

    /* width */
    &::-webkit-scrollbar {
      width: 12px;
    }

    /* Track */
    &::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.3);
    }

    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #eee;
      border-radius: 1em;
      border: 3px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
    }

    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #ccc;
      border: 3px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
    }
  }
}

.body {
  flex: 1;
  padding: 1.5em 1.5em 0 1.5em;
  color: #212121;
  display: flex;
  flex-direction: column;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 1.5em 0;
}

.spinner-icon {
  height: 2em;
  width: 2em;
  fill: #ccc;

  animation-name: spin;
  animation-duration: 1000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
